import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useContacts } from '../hooks';
import { Contact } from '../types';
import Autocomplete from '@mui/material/Autocomplete';
import { Paper, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SingleSelectGroup } from './SingleSelectGroup';
import { SingleSelectAction } from './SingleSelectAction';
import { isRelated } from '../utils';
import { QueryParams } from '../urls';
import { useDebouncedValue } from '~/src/hooks/useDebouncedValue';

export type ContactSelectProps = {
  value: Contact | null;
  onChange: (contact: Contact | null) => void;
  parameters?: Omit<Partial<QueryParams>, 'search'>;
  onAddContact: () => void;
  matterId: string | number;
};

export const ContactSelect = ({
  parameters = {},
  value,
  matterId,
  onChange,
  onAddContact,
}: ContactSelectProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const autocompleteRef = useRef<HTMLInputElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [open, setOpen] = useState(false);

  const labelForContact = ({ first_name, last_name }: Contact) =>
    [first_name, last_name].filter(Boolean).join(' ');
  const contacts = useContacts(matterId, {
    ...parameters,
    search: debouncedSearch,
  });

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) => {
        if (entry) {
          setIsIntersecting(entry.isIntersecting);
        }
      }),
    [],
  );

  useEffect(() => {
    if (autocompleteRef.current) {
      observer.observe(autocompleteRef.current);
    }
    return () => observer.disconnect();
  }, [autocompleteRef, observer]);

  useEffect(() => {
    if (open && !isIntersecting) {
      requestAnimationFrame(() => setOpen(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      PaperComponent={({ children, ...rest }) => (
        <Paper
          {...rest}
          onMouseDown={(e) => e.preventDefault()}
          sx={(theme) => ({
            marginTop: '4px',
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: '4px',
            boxShadow: '0px 4px 8px -2px #1018281A',
          })}
        >
          {children}
          <SingleSelectAction onClick={onAddContact} label={'New Contact'} />
        </Paper>
      )}
      inputValue={search}
      groupBy={(option) =>
        isRelated(option) ? 'RELATED CONTACTS' : 'OTHER CONTACTS'
      }
      renderGroup={(props) => (
        <SingleSelectGroup
          {...props}
          withDivider={props.group === 'RELATED CONTACTS'}
          key={props.key}
        />
      )}
      getOptionLabel={labelForContact}
      value={value}
      isOptionEqualToValue={(c1, c2) => c1.id === c2.id}
      renderOption={(props, o) => (
        <li
          {...props}
          data-test-class="contact-auto-complete-item"
          style={{ padding: 0 }}
        >
          <Typography
            variant="button"
            sx={(theme) => ({ padding: theme.spacing(2) })}
          >
            {labelForContact(o)}
          </Typography>
        </li>
      )}
      onChange={(_, c) => onChange(c)}
      options={contacts.data?.pages?.[0]?.contacts || []}
      loading={contacts.isLoading}
      onInputChange={(_, v) => setSearch(v)}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={autocompleteRef}
          placeholder="Attach contact"
          size={'small'}
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon color="action" fontSize="small" />,
            endAdornment: null,
          }}
        />
      )}
    />
  );
};
